import { useEffect, useRef } from 'react';

/**
 * Set up a `setInterval` whose callback is always the latest closure passed in,
 * without restarting the timer on every render. `enabled` controls the interval
 * lifetime — when it flips to `false` the timer is cleared and the callback
 * no longer fires (used by the status modal: the live countdown only runs
 * while the modal is open).
 *
 * Cleans up on unmount or when `enabled` / `delayMs` change. The callback
 * itself can change every render without restarting.
 */
export function useInterval(callback: () => void, delayMs: number, enabled = true): void {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || delayMs <= 0) {
      return;
    }
    const id = setInterval(() => {
      savedCallback.current();
    }, delayMs);
    return () => {
      clearInterval(id);
    };
  }, [delayMs, enabled]);
}
